import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Heart, Search, MapPin, DollarSign, Clock, Filter, X } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BrowseHealers = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [searchParams] = useSearchParams();
  const [healers, setHealers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [healerTypes, setHealerTypes] = useState([]);
  
  // Filters
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');
  const [selectedType, setSelectedType] = useState('');
  const [selectedLocation, setSelectedLocation] = useState('');
  const [minRate, setMinRate] = useState('');
  const [maxRate, setMaxRate] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    fetchHealerTypes();
    fetchHealers();
  }, []);

  const fetchHealerTypes = async () => {
    try {
      const response = await axios.get(`${API}/healer-types`);
      setHealerTypes(response.data);
    } catch (error) {
      console.error('Error fetching healer types:', error);
    }
  };

  const fetchHealers = async () => {
    setLoading(true);
    try {
      const params = {};
      if (selectedType) params.healer_type = selectedType;
      if (selectedLocation) params.location = selectedLocation;
      if (minRate) params.min_rate = minRate;
      if (maxRate) params.max_rate = maxRate;

      const response = await axios.get(`${API}/healers`, { params });
      let data = response.data;

      // Client-side search filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        data = data.filter(healer => 
          healer.healer_type?.toLowerCase().includes(query) ||
          healer.location?.toLowerCase().includes(query) ||
          healer.specialties?.some(s => s.toLowerCase().includes(query)) ||
          healer.bio?.toLowerCase().includes(query)
        );
      }

      setHealers(data);
    } catch (error) {
      toast.error('Failed to load healers');
    } finally {
      setLoading(false);
    }
  };

  const clearFilters = () => {
    setSearchQuery('');
    setSelectedType('');
    setSelectedLocation('');
    setMinRate('');
    setMaxRate('');
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchHealers();
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery, selectedType, selectedLocation, minRate, maxRate]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-amber-50 to-yellow-50">
      <Helmet>
        <title>Browse Healers | Ourmend</title>
        <meta name="description" content="Browse and book trusted healers of color. Filter by specialty, location, and rate. Find your perfect healer today." />
      </Helmet>
      {/* Navbar */}
      <nav className="bg-white/80 backdrop-blur-lg border-b border-gray-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-2 cursor-pointer" onClick={() => navigate('/')}>
            <Heart className="w-8 h-8 text-#8B4513" />
            <span className="text-2xl font-bold text-gray-900">Ourmend</span>
          </div>
          <div className="flex items-center space-x-4">
            {user ? (
              <>
                <Button
                  variant="ghost"
                  onClick={() => navigate(user.role === 'healer' ? '/healer-dashboard' : '/dashboard')}
                  data-testid="nav-dashboard-link"
                >
                  Dashboard
                </Button>
                <Button variant="outline" onClick={logout} data-testid="nav-logout-btn">
                  Logout
                </Button>
              </>
            ) : (
              <Button onClick={() => navigate('/')} className="bg-#8B4513 hover:bg-#6B3410" data-testid="nav-signin-link">
                Sign In
              </Button>
            )}
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Search & Filters Header */}
        <div className="mb-8 space-y-4">
          <div className="flex gap-3">
            <div className="flex-1 relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <Input
                placeholder="Search by type, specialty, or location..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-12 h-12 bg-white shadow-sm"
                data-testid="search-input"
              />
            </div>
            <Button
              variant="outline"
              onClick={() => setShowFilters(!showFilters)}
              className="h-12 px-6"
              data-testid="toggle-filters-btn"
            >
              <Filter className="w-5 h-5 mr-2" />
              Filters
            </Button>
          </div>

          {/* Filters Panel */}
          {showFilters && (
            <div className="glass-card p-6 space-y-4 slide-in">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-lg">Filter Healers</h3>
                <Button variant="ghost" size="sm" onClick={clearFilters} data-testid="clear-filters-btn">
                  <X className="w-4 h-4 mr-1" />
                  Clear All
                </Button>
              </div>

              <div className="grid md:grid-cols-4 gap-4">
                <div>
                  <label className="text-sm font-medium mb-2 block">Healer Type</label>
                  <Select value={selectedType} onValueChange={setSelectedType}>
                    <SelectTrigger data-testid="healer-type-select">
                      <SelectValue placeholder="All Types" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Types</SelectItem>
                      {healerTypes.map(type => (
                        <SelectItem key={type} value={type}>{type}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="text-sm font-medium mb-2 block">Location</label>
                  <Input
                    placeholder="City or State"
                    value={selectedLocation}
                    onChange={(e) => setSelectedLocation(e.target.value)}
                    data-testid="location-input"
                  />
                </div>

                <div>
                  <label className="text-sm font-medium mb-2 block">Min Rate ($/hr)</label>
                  <Input
                    type="number"
                    placeholder="0"
                    value={minRate}
                    onChange={(e) => setMinRate(e.target.value)}
                    data-testid="min-rate-input"
                  />
                </div>

                <div>
                  <label className="text-sm font-medium mb-2 block">Max Rate ($/hr)</label>
                  <Input
                    type="number"
                    placeholder="500"
                    value={maxRate}
                    onChange={(e) => setMaxRate(e.target.value)}
                    data-testid="max-rate-input"
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Results */}
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-gray-900">
            {loading ? 'Loading...' : `${healers.length} Healers Found`}
          </h2>
        </div>

        {/* Healers Grid */}
        {loading ? (
          <div className="flex justify-center items-center py-20">
            <div className="spinner"></div>
          </div>
        ) : healers.length === 0 ? (
          <div className="text-center py-20" data-testid="no-healers-message">
            <p className="text-xl text-gray-600">No healers found. Try adjusting your filters.</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {healers.map((healer) => (
              <Card
                key={healer.id}
                className="overflow-hidden hover:shadow-xl transition-shadow cursor-pointer"
                onClick={() => navigate(`/healer/${healer.user_id}`)}
                data-testid={`healer-card-${healer.user_id}`}
              >
                <div className="h-48 bg-gradient-to-br from-teal-400 to-amber-500 relative">
                  {healer.profile_image && (
                    <img
                      src={healer.profile_image.startsWith('http') ? healer.profile_image : `${API}/files/${healer.profile_image}`}
                      alt={healer.user?.name}
                      className="w-full h-full object-cover"
                    />
                  )}
                  <div className="absolute top-4 right-4">
                    <Badge className="bg-white text-#6B3410">{healer.healer_type}</Badge>
                  </div>
                </div>

                <div className="p-6 space-y-3">
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">{healer.user?.name || 'Unknown'}</h3>
                    <p className="text-sm text-gray-600">{healer.years_experience} years experience</p>
                  </div>

                  <p className="text-gray-700 line-clamp-2">{healer.bio}</p>

                  {healer.specialties && healer.specialties.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {healer.specialties.slice(0, 3).map((specialty, idx) => (
                        <Badge key={idx} variant="outline" className="text-xs">
                          {specialty}
                        </Badge>
                      ))}
                    </div>
                  )}

                  <div className="flex items-center justify-between pt-3 border-t">
                    <div className="flex items-center text-gray-600">
                      <MapPin className="w-4 h-4 mr-1" />
                      <span className="text-sm">{healer.location}</span>
                    </div>
                    <div className="flex items-center text-#8B4513 font-semibold">
                      <DollarSign className="w-4 h-4" />
                      <span>{healer.hourly_rate}/hr</span>
                    </div>
                  </div>

                  <Button className="w-full bg-#8B4513 hover:bg-#6B3410" data-testid={`view-profile-btn-${healer.user_id}`}>
                    View Profile
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default BrowseHealers;